import React, { useState, useEffect, useCallback } from 'react';
import { hrApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Sparkle, Check, X, Trash, User as UserIcon } from '@phosphor-icons/react';
import { ObjectPageHeader, SectionTabs, FioriTable, StatusPill, STATUS_COLOR } from '../components/fiori';

const TABS = [
  { key: 'employees',  label: 'Employees' },
  { key: 'leave',      label: 'Leave' },
  { key: 'reviews',    label: 'Performance' },
  { key: 'payroll',    label: 'Payroll' },
];

export default function HRPage() {
  const [active, setActive] = useState('employees');
  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null);

  const loadSummary = useCallback(async () => {
    try { const r = await hrApi.summary(); setSummary(r.data); } catch {}
  }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      let r;
      if (active === 'employees') r = await hrApi.employees.list();
      else if (active === 'leave') r = await hrApi.leaveRequests.list();
      else if (active === 'reviews') r = await hrApi.reviews.list();
      else if (active === 'payroll') r = await hrApi.payroll.list();
      setRows(r?.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [active]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { load(); }, [load]);

  const kpis = [
    { label: 'Active employees', value: summary.active_employees ?? '–' },
    { label: 'On leave',          value: summary.on_leave ?? '–',     color: '#FFC72C' },
    { label: 'Pending requests',  value: summary.pending_leave ?? '–', color: '#41B6E6' },
    { label: 'Open reviews',      value: summary.open_reviews ?? '–',  color: '#8347AD' },
  ];

  return (
    <div data-testid="hr-page">
      <ObjectPageHeader
        breadcrumb={['Home', 'Human Resources']}
        title="Human Resources"
        subtitle="Employee directory, leave management, performance cycles and payroll"
        kpis={kpis}
        actions={
          <Button
            className="rounded-sm h-9"
            style={{ background: '#002677' }}
            onClick={() => setDialog({
              employees: 'employee', leave: 'leave', reviews: 'review', payroll: 'payroll'
            }[active])}
            data-testid="hr-create-btn"
          >
            <Plus size={16} className="mr-1.5" /> New
          </Button>
        }
      />
      <SectionTabs tabs={TABS} active={active} onChange={setActive} />
      <div className="p-6">
        {loading ? <div className="text-center py-10 text-sm text-muted-foreground">Loading…</div> : (
          <>
            {active === 'employees' && <EmployeesTable rows={rows} reload={load} />}
            {active === 'leave'     && <LeaveTable rows={rows} reload={load} />}
            {active === 'reviews'   && <ReviewsTable rows={rows} />}
            {active === 'payroll'   && <PayrollTable rows={rows} />}
          </>
        )}
      </div>
      {dialog === 'employee' && <EmployeeForm onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); loadSummary(); }} />}
      {dialog === 'leave'    && <LeaveForm onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); loadSummary(); }} />}
      {dialog === 'review'   && <ReviewForm onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); loadSummary(); }} />}
      {dialog === 'payroll'  && <PayrollForm onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
    </div>
  );
}

function EmployeesTable({ rows, reload }) {
  const del = async (id) => {
    try { await hrApi.employees.delete(id); toast.success('Deleted'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <FioriTable
      columns={[
        { key: 'employee_id', label: 'ID', width: '90px' },
        { key: 'name', label: 'Name', render: r => (
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-full bg-[#002677] text-white text-[10px] font-semibold flex items-center justify-center">
                {(r.name || '').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-medium truncate">{r.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{r.email}</div>
              </div>
            </div>
          ) },
        { key: 'role',       label: 'Role' },
        { key: 'department', label: 'Department' },
        { key: 'contract_type', label: 'Contract', render: r => r.contract_type?.replace('_', ' ') },
        { key: 'start_date', label: 'Start',      render: r => r.start_date?.slice(0, 10) },
        { key: 'status',     label: 'Status',     render: r => <StatusPill status={r.status} /> },
        { key: 'actions', label: '', width: '50px', render: r => <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => del(r.id)}><Trash size={14} /></Button> },
      ]}
      rows={rows}
      rowStatusColor={(r) => STATUS_COLOR[r.status] || '#8A8E99'}
      emptyLabel="No employees — click New to add the first one"
    />
  );
}

function LeaveTable({ rows, reload }) {
  const [aiFor, setAiFor] = useState(null);
  const [aiText, setAiText] = useState('');
  const setStatus = async (id, status) => {
    try { await hrApi.leaveRequests.setStatus(id, status); toast.success(`Leave ${status}`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const aiSummary = async (id) => {
    setAiFor(id); setAiText('Thinking…');
    try {
      const { data } = await hrApi.leaveRequests.aiSummary(id);
      setAiText(data.ai_summary); reload();
    } catch { setAiText('AI failed'); }
  };
  return (
    <>
      <FioriTable
        columns={[
          { key: 'employee_name', label: 'Employee' },
          { key: 'leave_type', label: 'Type', render: r => <span className="capitalize">{r.leave_type}</span> },
          { key: 'start_date', label: 'From', render: r => r.start_date?.slice(0, 10) },
          { key: 'end_date',   label: 'To',   render: r => r.end_date?.slice(0, 10) },
          { key: 'days',       label: 'Days' },
          { key: 'reason',     label: 'Reason', render: r => <span className="text-muted-foreground truncate block max-w-xs">{r.reason || '—'}</span> },
          { key: 'status',     label: 'Status', render: r => <StatusPill status={r.status} /> },
          { key: 'actions', label: '', width: '260px', render: r => (
              r.status === 'pending' ? (
                <div className="flex gap-1 justify-end">
                  <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={() => aiSummary(r.id)}>
                    <Sparkle size={12} className="mr-1" />AI
                  </Button>
                  <Button size="sm" className="h-7 text-xs rounded-sm" style={{ background: '#00B140' }} onClick={() => setStatus(r.id, 'approved')}><Check size={12} /></Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={() => setStatus(r.id, 'rejected')}><X size={12} /></Button>
                </div>
              ) : null
            ) },
        ]}
        rows={rows}
        rowStatusColor={(r) => STATUS_COLOR[r.status] || '#8A8E99'}
        emptyLabel="No leave requests"
      />
      {aiFor && (
        <Dialog open onOpenChange={() => setAiFor(null)}>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkle size={18} className="text-[#002677]" />AI leave assessment</DialogTitle></DialogHeader>
            <pre className="whitespace-pre-wrap text-sm p-3 bg-muted/30 rounded-sm">{aiText}</pre>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function ReviewsTable({ rows }) {
  return (
    <FioriTable
      columns={[
        { key: 'cycle', label: 'Cycle', width: '100px' },
        { key: 'employee_name', label: 'Employee' },
        { key: 'rating', label: 'Rating', render: r => r.rating ? '★'.repeat(r.rating) + '☆'.repeat(5 - r.rating) : '—' },
        { key: 'goals', label: 'Goals', render: r => `${r.goals?.length || 0} set` },
        { key: 'status', label: 'Status', render: r => <StatusPill status={r.status === 'draft' ? 'draft' : r.status === 'submitted' ? 'sent' : 'approved'} /> },
        { key: 'created_at', label: 'Created', render: r => r.created_at?.slice(0, 10) },
      ]}
      rows={rows}
      rowStatusColor={(r) => r.status === 'draft' ? '#8A8E99' : r.status === 'submitted' ? '#41B6E6' : '#00B140'}
      emptyLabel="No performance reviews"
    />
  );
}

function PayrollTable({ rows }) {
  const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
  return (
    <FioriTable
      columns={[
        { key: 'period', label: 'Period' },
        { key: 'employees_count', label: 'Employees' },
        { key: 'gross_total', label: 'Gross', render: r => fmt(r.gross_total) },
        { key: 'tax_total',   label: 'Tax',   render: r => <span style={{ color: '#E4002B' }}>{fmt(r.tax_total)}</span> },
        { key: 'net_total',   label: 'Net',   render: r => <span className="font-semibold" style={{ color: '#00B140' }}>{fmt(r.net_total)}</span> },
        { key: 'status',      label: 'Status', render: r => <StatusPill status={r.status} /> },
      ]}
      rows={rows}
      rowStatusColor={(r) => STATUS_COLOR[r.status] || '#002677'}
      emptyLabel="No payroll runs yet"
    />
  );
}

/* Forms */

function BaseDialog({ title, onClose, children, onSubmit, submitting, submitLabel = 'Create' }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-sm">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 py-2">
          {children}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button type="submit" className="rounded-sm" style={{ background: '#002677' }} disabled={submitting}>
              {submitting ? 'Saving…' : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function EmployeeForm({ onClose, onSaved }) {
  const [form, setForm] = useState({
    employee_id: '', name: '', email: '', department: '', role: '',
    contract_type: 'full_time', start_date: new Date().toISOString().slice(0, 10),
    location: '', salary_band: '',
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await hrApi.employees.create(form); toast.success('Employee added'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <BaseDialog title="New employee" onClose={onClose} onSubmit={submit} submitting={saving}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Employee ID"><Input value={form.employee_id} onChange={e => setForm({ ...form, employee_id: e.target.value })} required /></Field>
        <Field label="Name"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></Field>
      </div>
      <Field label="Email"><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Role"><Input value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} required /></Field>
        <Field label="Department"><Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Contract">
          <Select value={form.contract_type} onValueChange={v => setForm({ ...form, contract_type: v })}>
            <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="full_time">Full time</SelectItem>
              <SelectItem value="part_time">Part time</SelectItem>
              <SelectItem value="contractor">Contractor</SelectItem>
              <SelectItem value="intern">Intern</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Start date"><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Location"><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} /></Field>
        <Field label="Monthly salary (€)"><Input type="number" value={form.salary_band} onChange={e => setForm({ ...form, salary_band: e.target.value })} placeholder="Used for payroll" /></Field>
      </div>
    </BaseDialog>
  );
}

function LeaveForm({ onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ employee_id: '', leave_type: 'vacation', start_date: today, end_date: today, reason: '' });
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { hrApi.employees.list().then(r => setEmployees(r.data)).catch(() => {}); }, []);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await hrApi.leaveRequests.create(form); toast.success('Leave requested'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <BaseDialog title="New leave request" onClose={onClose} onSubmit={submit} submitting={saving} submitLabel="Submit">
      <Field label="Employee">
        <Select value={form.employee_id} onValueChange={v => setForm({ ...form, employee_id: v })}>
          <SelectTrigger className="rounded-sm"><SelectValue placeholder="Pick employee" /></SelectTrigger>
          <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Type">
        <Select value={form.leave_type} onValueChange={v => setForm({ ...form, leave_type: v })}>
          <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="vacation">Vacation</SelectItem>
            <SelectItem value="sick">Sick</SelectItem>
            <SelectItem value="personal">Personal</SelectItem>
            <SelectItem value="parental">Parental</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="From"><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required /></Field>
        <Field label="To"><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} required /></Field>
      </div>
      <Field label="Reason"><Textarea rows={2} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} /></Field>
    </BaseDialog>
  );
}

function ReviewForm({ onClose, onSaved }) {
  const [form, setForm] = useState({ employee_id: '', reviewer_id: '', cycle: `${new Date().getFullYear()}-Q${Math.ceil((new Date().getMonth() + 1) / 3)}`, rating: 3, strengths: '', improvements: '' });
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { hrApi.employees.list().then(r => setEmployees(r.data)).catch(() => {}); }, []);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await hrApi.reviews.create({ ...form, rating: parseInt(form.rating, 10) || null, goals: [] });
      toast.success('Review started'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <BaseDialog title="New performance review" onClose={onClose} onSubmit={submit} submitting={saving}>
      <Field label="Employee">
        <Select value={form.employee_id} onValueChange={v => setForm({ ...form, employee_id: v })}>
          <SelectTrigger className="rounded-sm"><SelectValue placeholder="Pick employee" /></SelectTrigger>
          <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Reviewer (employee ID)"><Input value={form.reviewer_id} onChange={e => setForm({ ...form, reviewer_id: e.target.value })} required /></Field>
        <Field label="Cycle"><Input value={form.cycle} onChange={e => setForm({ ...form, cycle: e.target.value })} required /></Field>
      </div>
      <Field label="Rating (1-5)"><Input type="number" min="1" max="5" value={form.rating} onChange={e => setForm({ ...form, rating: e.target.value })} /></Field>
      <Field label="Strengths"><Textarea rows={2} value={form.strengths} onChange={e => setForm({ ...form, strengths: e.target.value })} /></Field>
      <Field label="Areas for improvement"><Textarea rows={2} value={form.improvements} onChange={e => setForm({ ...form, improvements: e.target.value })} /></Field>
    </BaseDialog>
  );
}

function PayrollForm({ onClose, onSaved }) {
  const thisMonth = new Date().toISOString().slice(0, 7);
  const [period, setPeriod] = useState(thisMonth);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { data } = await hrApi.payroll.create({ period });
      toast.success(`Payroll processed for ${data.employees_count} employees`); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <BaseDialog title="Run payroll" onClose={onClose} onSubmit={submit} submitting={saving} submitLabel="Process">
      <p className="text-sm text-muted-foreground">
        Generates payslips for all active employees using their monthly salary. Applies a flat 20% tax
        placeholder — you can refine the tax engine later.
      </p>
      <Field label="Period (YYYY-MM)"><Input value={period} onChange={e => setPeriod(e.target.value)} required /></Field>
    </BaseDialog>
  );
}
